import type { ClientLocationPlanBreakdown } from "api-types/locations/locationsResponses";

export interface PlanBreakdownDisplayRow {
  planName: string;
  count: number;
  percentage: number;
  isUnused: boolean;
}

export interface PlanBreakdownOtherRow {
  planCount: number;
  count: number;
  percentage: number;
}

export interface PlanBreakdownView {
  rankedRows: PlanBreakdownDisplayRow[];
  unusedRows: PlanBreakdownDisplayRow[];
  otherRow: PlanBreakdownOtherRow | undefined;
  expanderLabel: string | undefined;
}

/**
 * Derives the visible plan ranking and remainder row for a plan breakdown table.
 *
 * Ranked and unused rows stay separate so the caller can place the remainder between them: it
 * aggregates plans that have homes, so it must not read as part of the zero-count tail.
 */
export function derivePlanBreakdownView(breakdown: ClientLocationPlanBreakdown, expanded: boolean): PlanBreakdownView {
  const visible = expanded ? breakdown.planBreakdownRows : breakdown.planBreakdownRows.slice(0, 5);
  const rankedRows: PlanBreakdownDisplayRow[] = visible.map((row) => ({ ...row, isUnused: false }));
  const unusedRows: PlanBreakdownDisplayRow[] = expanded
    ? breakdown.unusedPlanNames.map((planName) => ({ planName, count: 0, percentage: 0, isUnused: true }))
    : [];

  const visibleHomes = visible.reduce((total, row) => total + row.count, 0);
  const otherCount = breakdown.totalHomes - visibleHomes;
  const otherPlanCount = breakdown.totalPlanTypes - visible.length;
  const otherRow =
    otherCount > 0 && otherPlanCount > 0
      ? {
          planCount: otherPlanCount,
          count: otherCount,
          percentage: (otherCount / breakdown.totalHomes) * 100,
        }
      : undefined;

  let expanderLabel: string | undefined;
  const hasMoreRows = breakdown.planBreakdownRows.length > 5 || breakdown.unusedPlanNames.length > 0;
  if (hasMoreRows) {
    expanderLabel = expanded ? "See fewer" : "See more plans";
  }

  return { rankedRows, unusedRows, otherRow, expanderLabel };
}
